import { pick } from 'ramda'
import { useMemo } from 'react'
import { useDomesticV1CartQuery } from '~/app/react-query/domestic/cart/v1'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'

/**
 * 장바구니 > 가격 계산 Hook
 */
const useDomesticCartPrice = () => {
  const { data } = useDomesticV1CartQuery()
  const { selectedOptionIds } = useDomesticCartStore(pick(['selectedOptionIds']))

  const cartItems = data?.productSection.cartItems

  // 선택된 옵션들만 필터링 (구매 가능한 옵션만)
  const selectedCartOptions = useMemo(() => {
    if (!cartItems) return []

    return cartItems
      .map(item => ({
        ...item,
        options: item.options.filter(
          option => selectedOptionIds.has(option.optionId) && option.isSaleAvailable
        ),
      }))
      .filter(item => item.options.length > 0) // 선택된 옵션이 있는 아이템만 포함
  }, [selectedOptionIds, cartItems])

  const { totalPrice, totalMemberDiscountPrice } = useMemo(() => {
    // flatMap 대신 map을 사용하여 부모 아이템의 type 정보를 유지
    const flattenOptionsWithType = selectedCartOptions.flatMap(item =>
      item.options.map(option => ({ option, type: item.type }))
    )

    return flattenOptionsWithType.reduce(
      (acc, { option: cur, type }) => {
        // 레저 카테고리인 경우 수량을 계산
        const isLeisure = type === 'DOMESTIC_LEISURE' && 'quantity' in cur
        acc.totalPrice += cur.price * (isLeisure ? cur.quantity : 1)

        // 국내숙소인 경우 회원등급으로 인한 할인 가격을 계산
        const isProperty = type === 'DOMESTIC_RESERVATION'
        if (isProperty && 'originalPrice' in cur) {
          acc.totalMemberDiscountPrice += (cur.originalPrice as number) - cur.price
        }

        return acc
      },
      {
        totalPrice: 0,
        totalMemberDiscountPrice: 0,
      }
    )
  }, [selectedCartOptions])

  return {
    // 선택한 옵션들의 전체 정보
    selectedCartOptions,
    // 선택한 옵션들의 총 상품 가격
    totalPrice,
    // 선택한 옵션들의 회원등급으로 인한 할인 가격
    totalMemberDiscountPrice,
  }
}

export default useDomesticCartPrice
